import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  addProjectClosureChecklist as AddProjectClosureChecklistType,
  ChangeRequestProps,
  ClosureFileUpload,
  ProjectClosureChecklistItem,
  ProjectClosureChecklistResponse,
  ProjectClosureChecklistState,
  ProjectClosureResponse,
  UpdateChecklistRequest,
} from '../../../../../types/ProjectManagement/Project/ProjectView/ProjectClosure/projectClosureChecklistTypes'
import { AppDispatch, RootState } from '../../../../../stateStore'
import { ApiLoadingState } from '../../../../../middleware/api/apiList'
import projectClosureChecklistApi from '../../../../../middleware/api/ProjectManagement/Projects/ProjectView/ProjectClosure/projectClosureChecklistApi'
import { LoadingState, ValidationError } from '../../../../../types/commonTypes'

export const getProjectAllClosureChecklists = createAsyncThunk(
  'projectClosureChecklist/getAll',
  async (props: ChangeRequestProps, thunkApi) => {
    try {
      return await projectClosureChecklistApi.getProjectAllClosureChecklists(
        props,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
export const getProjectClosureRequest = createAsyncThunk(
  'projectClosureChecklist/getProjectClosureRequest',
  async (props: ChangeRequestProps, thunkApi) => {
    try {
      return await projectClosureChecklistApi.getProjectClosureRequest(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const fetchProjectClosureChecklist = createAsyncThunk(
  'projectClosureChecklist/fetch',
  async (_, thunkApi) => {
    try {
      return await projectClosureChecklistApi.getProjectClosureChecklist()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const updateProjectClosureChecklist = createAsyncThunk<
  number | undefined,
  UpdateChecklistRequest,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectClosureChecklist/update', async (props, thunkApi) => {
  try {
    return await projectClosureChecklistApi.updateProjectClosureChecklist(props)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

export const getPermissionsOfProjectClosure = createAsyncThunk<
  boolean,
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectClosureChecklist/getPermissions', async (projectId, thunkApi) => {
  try {
    const resp =
      await projectClosureChecklistApi.getPermissionsOfProjectClosure(projectId)
    if (typeof resp === 'boolean') return resp
    if (typeof resp === 'string') return resp.toLowerCase() === 'true'
    return Boolean(resp)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

export const addProjectClosureChecklist = createAsyncThunk<
  number | undefined,
  AddProjectClosureChecklistType,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectClosureChecklist/add',
  async (
    addProjectClosureDetails: AddProjectClosureChecklistType,
    thunkApi,
  ) => {
    try {
      return await projectClosureChecklistApi.addProjectClosureChecklist(
        addProjectClosureDetails,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const getProjectClosureHistoryDetails = createAsyncThunk<
  ProjectClosureResponse | undefined,
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectClosureChecklist/getClosureHistory',
  async (closureId: string, thunkApi) => {
    try {
      return await projectClosureChecklistApi.projectClosureHistoryDetails(
        closureId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const uploadProjectClosureFile = createAsyncThunk(
  'projectClosureChecklist/uploadFile',
  async (props: ClosureFileUpload, thunkApi) => {
    try {
      return await projectClosureChecklistApi.uploadProjectClosureFile(
        props.file,
        props.id,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const downloadProjectClosureChecklist = createAsyncThunk(
  'projectClosureChecklist/download',
  async (id: number, thunkApi) => {
    try {
      return await projectClosureChecklistApi.getProjectClosureUploadFile(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const getIsClosureEnable = createAsyncThunk<
  boolean,
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectClosureChecklist/getIsClosureEnable', async (projectId, thunkApi) => {
  try {
    return await projectClosureChecklistApi.getIsClosureEnable(projectId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const initialState: ProjectClosureChecklistState = {
  items: [],
  projectClosureList: [],
  ProjectClosure: [],
  isLoading: ApiLoadingState.idle,
  currentProjectId: undefined,
  closureHistoryDetails: undefined,
  closureId: undefined,
  closureFile: undefined,
  closureFileInfo: undefined,
  projectClosurePermissions: false,
  isClosureEnable: false,
}

const projectClosureChecklistSlice = createSlice({
  name: 'projectClosureChecklist',
  initialState,
  reducers: {
    setCurrentProjectId: (state, action) => {
      state.currentProjectId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addProjectClosureChecklist.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(addProjectClosureChecklist.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        if (typeof action.payload === 'number') {
          state.closureId = action.payload
        }
      })
      .addCase(addProjectClosureChecklist.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getProjectAllClosureChecklists.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getProjectAllClosureChecklists.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.projectClosureList = action.payload
      })
      .addCase(getProjectAllClosureChecklists.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(fetchProjectClosureChecklist.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(fetchProjectClosureChecklist.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.items = action.payload
      })
      .addCase(fetchProjectClosureChecklist.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getProjectClosureRequest.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getProjectClosureRequest.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.ProjectClosure = action.payload
      })
      .addCase(getProjectClosureRequest.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getProjectClosureHistoryDetails.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getProjectClosureHistoryDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.closureHistoryDetails = action.payload as
          | ProjectClosureResponse
          | undefined
      })
      .addCase(getProjectClosureHistoryDetails.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getPermissionsOfProjectClosure.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getPermissionsOfProjectClosure.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.projectClosurePermissions = action.payload as boolean
      })
      .addCase(getPermissionsOfProjectClosure.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(downloadProjectClosureChecklist.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
        state.closureFile = undefined
        // Revoke previous object URL if any
        if (state.closureFileInfo?.url) {
          try {
            URL.revokeObjectURL(state.closureFileInfo.url)
          } catch (_) {
            /* noop */
          }
        }
        state.closureFileInfo = undefined
      })
      .addCase(downloadProjectClosureChecklist.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.closureFile = undefined
        const blob = action.payload as Blob | undefined
        if (blob) {
          const url = URL.createObjectURL(blob)
          let ext = 'bin'
          if (blob.type === 'application/pdf') ext = 'pdf'
          else if (blob.type.includes('wordprocessingml.document')) ext = 'docx'
          else if (
            blob.type.includes('excel') ||
            blob.type.includes('spreadsheetml') ||
            blob.type === 'application/vnd.ms-excel'
          )
            ext = 'xlsx'
          const filename = `project_closure_${action.meta.arg}.${ext}`
          state.closureFileInfo = { url, filename }
        }
      })
      .addCase(downloadProjectClosureChecklist.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
        state.closureFile = undefined
        state.closureFileInfo = undefined
      })
      .addCase(getIsClosureEnable.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getIsClosureEnable.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.isClosureEnable = action.payload as boolean
      })
      .addCase(getIsClosureEnable.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.projectClosureChecklist.isLoading

const checkList = (state: RootState): ProjectClosureChecklistItem[] =>
  state.projectClosureChecklist.items

const ProjectClosure = (state: RootState): ProjectClosureChecklistItem[] =>
  state.projectClosureChecklist.ProjectClosure

const closureId = (state: RootState): number | string | undefined =>
  state.projectClosureChecklist.closureId

const allCheckListItems = (
  state: RootState,
): ProjectClosureChecklistResponse[] =>
  state.projectClosureChecklist.projectClosureList

const currentProjectId = (state: RootState): string | undefined =>
  state.projectClosureChecklist.currentProjectId

const projectClosureHistory = (
  state: RootState,
): ProjectClosureResponse | undefined =>
  state.projectClosureChecklist.closureHistoryDetails

const closureFile = (state: RootState): Blob | undefined =>
  state.projectClosureChecklist.closureFile

const closureFileInfo = (
  state: RootState,
): { url: string; filename: string } | undefined =>
  state.projectClosureChecklist.closureFileInfo

const projectPermission = (state: RootState): boolean =>
  state.projectClosureChecklist.projectClosurePermissions

const isClosureEnable = (state: RootState): boolean =>
  state.projectClosureChecklist.isClosureEnable

export const projectClosureChecklistSelectors = {
  checkList,
  isLoading,
  ProjectClosure,
  closureId,
  allCheckListItems,
  currentProjectId,
  projectClosureHistory,
  closureFile,
  closureFileInfo,
  projectPermission,
  isClosureEnable,
}
const projectClosureRequestThunk = {
  addProjectClosureChecklist,
  getProjectAllClosureChecklists,
  getProjectClosureRequest,
  updateProjectClosureChecklist,
  getProjectClosureHistoryDetails,
  uploadProjectClosureFile,
  downloadProjectClosureChecklist,
  getPermissionsOfProjectClosure,
  getIsClosureEnable,
}

export const projectClosureChecklistService = {
  ...projectClosureRequestThunk,
  fetchProjectClosureChecklist,
  actions: projectClosureChecklistSlice.actions,
  selectors: projectClosureChecklistSelectors,
}

export default projectClosureChecklistSlice.reducer
